import React, { useMemo } from 'react';
import { ArrowDownCircle, Layers, Plus, Mountain, Droplets, CalendarDays, Trash2, AlertCircle, CheckCircle, Flame, ArrowUpCircle, Scale, ArrowUp, ArrowDown } from 'lucide-react';
import { Card } from '../ui/Card';
import { SectionHeader } from '../ui/SectionHeader';
import { MasterTipsCard } from '../ui/MasterTipsCard';
import { formatCurrency, roundMoney } from '../../utils/finance';

export const WaterfallTab = ({ data, actions, calculations }) => {
    const { surplusCalculations, waterfallVisuals } = calculations;

    // --- SINKING FUNDS LOGIC ---
    const sinkingFundAnalysis = useMemo(() => {
        let totalMonthlyNeed = 0;
        const analyzedFunds = (data.sinkingFunds || []).map(fund => {
            const target = parseFloat(fund.amount) || 0;
            const current = parseFloat(fund.current) || 0;
            const remaining = Math.max(0, target - current);
            
            let monthsLeft = 1;
            let isExpired = false;

            if (fund.date) {
                const today = new Date();
                const due = new Date(fund.date);
                const diffTime = due - today;
                // Rough estimate of months remaining
                monthsLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30)); 
                if (diffTime < 0) isExpired = true;
                if (monthsLeft < 1) monthsLeft = 1; 
            }

            // Round the monthly contribution requirement to 2 decimals immediately
            const monthlyContribution = roundMoney(remaining / monthsLeft);
            
            // Only add to "Need" if it is NOT already committed to expenses
            if (!fund.isCommitted) {
                totalMonthlyNeed += monthlyContribution;
            }

            return { ...fund, remaining, monthsLeft, monthlyContribution, progress: target > 0 ? (current/target)*100 : 0, isExpired };
        });

        return { analyzedFunds, totalMonthlyNeed: roundMoney(totalMonthlyNeed) };
    }, [data.sinkingFunds]);

    // Calculate "True" Surplus (Operational - Sinking Fund Needs)
    const trueSurplus = roundMoney(surplusCalculations.operational - sinkingFundAnalysis.totalMonthlyNeed);

    // Helper for visual state
    const isBalanced = Math.abs(trueSurplus) === 0;
    const isShortfall = trueSurplus < 0;
    const isSurplus = trueSurplus > 0;

    // --- ACTIONS ---

    // 1. Commit Sinking Fund (Existing)
    const handleCommitToFlow = (fund) => {
        actions.addArrayItem('expenseItems', {
            id: Date.now(),
            name: `Sinking Fund: ${fund.name}`,
            amount: fund.monthlyContribution,
            frequency: 'monthly',
            type: 'Fixed',
            dayDue: 1,
            linkedSinkingFundId: fund.id 
        });
        actions.updateArrayItem('sinkingFunds', fund.id, 'isCommitted', true);
    };

    // 2. Commit Waterfall Bucket (Updated Visuals H5)
    const handleCommitBucket = (bucket) => {
        if (!bucket.filled || bucket.filled <= 0) return;

        if (!bucket.linkedAssetId) {
            alert("Please link this bucket to an Asset (in the left column) before committing to flow.");
            return;
        }

        const confirmMsg = `Convert this allocation into a mandatory monthly expense?\n\n` +
                           `Amount: ${formatCurrency(bucket.filled)}\n` +
                           `Flow To: ${data.assets.find(a => a.id === bucket.linkedAssetId)?.name}\n\n` +
                           `This will reduce your 'Surplus' and increase your 'Burn'.`;

        if (window.confirm(confirmMsg)) {
            actions.addArrayItem('expenseItems', {
                id: Date.now(),
                name: `Savings: ${bucket.name}`,
                amount: bucket.filled,
                frequency: 'monthly',
                type: 'Fixed',
                dayDue: 1, // Default to 1st of month
                linkedAssetId: bucket.linkedAssetId
            });
        }
    };

    // 3. Reorder Logic (H7)
    const handleReorder = (id, direction) => {
        const sorted = [...data.waterfall].sort((a,b) => a.priority - b.priority);
        const index = sorted.findIndex(i => i.id === id);
        
        if (index === -1) return;
        if (direction === 'up' && index === 0) return; // Already top
        if (direction === 'down' && index === sorted.length - 1) return; // Already bottom

        const neighborIndex = direction === 'up' ? index - 1 : index + 1;
        
        // Swap priorities
        const currentItem = sorted[index];
        const neighborItem = sorted[neighborIndex];
        
        const tempPriority = currentItem.priority;
        
        // We use updateArrayItem to trigger the state change. 
        // Note: In a real DB, we might batch this, but here we do two updates.
        actions.updateArrayItem('waterfall', currentItem.id, 'priority', neighborItem.priority);
        actions.updateArrayItem('waterfall', neighborItem.id, 'priority', tempPriority);
    };

    const scrollToPriorities = () => {
        const el = document.getElementById('waterfall-priorities');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className="animate-fade-in">
            
            <MasterTipsCard 
                title="Reservoirs & Waterfalls" 
                subtitle="Direct the flow. Fill reservoirs before overflowing to waterfalls." 
                element="water"
            >
                Flow determines where your surplus goes. First, fill your <strong>Reservoirs</strong> (Sinking Funds) for known future expenses (like Holidays or Taxes). 
                Then, let the excess cascade down your <strong>Waterfalls</strong> (Savings & Investments) based on the priorities you set below.
            </MasterTipsCard>

            {/* SCENARIO A: SURPLUS DETECTED (Flow) */}
            {isSurplus && (
                <div className="mb-8 p-6 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl shadow-lg shadow-blue-500/20 text-white flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in">
                    <div>
                        <h3 className="text-2xl font-bold flex items-center gap-2">
                            <Droplets className="w-6 h-6 animate-bounce" /> 
                            {formatCurrency(trueSurplus)} Ready to Flow
                        </h3>
                        <p className="text-blue-100 text-sm mt-1">
                            You have unallocated capital. Check your priorities below to assign this wealth.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={scrollToPriorities} className="bg-white text-blue-600 font-bold px-4 py-2 rounded-lg shadow hover:bg-blue-50 transition-colors text-xs">
                            Review Priorities
                        </button>
                    </div>
                </div>
            )}

            {/* SCENARIO B: BALANCED (Perfect) */}
            {isBalanced && (
                <div className="mb-8 p-6 bg-gradient-to-r from-emerald-600 to-teal-500 rounded-2xl shadow-lg shadow-emerald-500/20 text-white flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in border border-emerald-400/30">
                    <div>
                        <h3 className="text-2xl font-bold flex items-center gap-2 text-white">
                            <Scale className="w-6 h-6" /> 
                            Perfectly Balanced
                        </h3>
                        <p className="text-emerald-100 text-sm mt-1">
                            Every dollar has a job. Your Reservoirs are fed and your Flow is optimized.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <div className="bg-black/20 px-4 py-2 rounded-lg text-xs font-mono text-emerald-100">
                            $0.00 Unallocated
                        </div>
                    </div>
                </div>
            )}

            {/* SCENARIO C: DEFICIT DETECTED (Drought) */}
            {isShortfall && (
                <div className="mb-8 p-6 bg-gradient-to-r from-red-900 to-orange-900 rounded-2xl shadow-lg shadow-red-500/20 text-white flex flex-col md:flex-row items-center justify-between gap-4 animate-fade-in border border-red-500/30">
                    <div>
                        <h3 className="text-2xl font-bold flex items-center gap-2 text-red-200">
                            <AlertCircle className="w-6 h-6 animate-pulse" /> 
                            {formatCurrency(Math.abs(trueSurplus))} Shortfall
                        </h3>
                        <p className="text-red-200/70 text-sm mt-1">
                            Your obligations exceed your income this month. You need to withdraw from a Reservoir (Emergency Fund).
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <div className="bg-black/30 px-4 py-2 rounded-lg text-xs text-red-200">
                            Check "The Reservoir" below to find funds.
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                
                {/* LEFT COLUMN: Waterfalls (Priority Logic) */}
                <Card element="water">
                    <div id="waterfall-priorities"></div>
                    <SectionHeader icon={ArrowDownCircle} title="Surplus Waterfalls" subtitle="Where does the overflow go?" element="water" />
                    
                    {/* True Surplus Indicator */}
                    <div className="mb-4 bg-slate-950 p-3 rounded-lg border border-slate-800 flex justify-between items-center">
                        <div>
                            <span className="text-xs text-slate-400 block">True Allocatable Flow</span>
                            <span className="text-xs text-slate-500">(Surplus - Uncommitted Reservoirs)</span>
                        </div>
                        <div className={`text-xl font-bold ${isSurplus ? 'text-blue-400' : isShortfall ? 'text-red-400' : 'text-emerald-400'}`}>
                            {formatCurrency(trueSurplus)}
                        </div>
                    </div>

                    <div className="space-y-4">
                    {data.waterfall.sort((a,b) => a.priority - b.priority).map((item, index) => (
                        <div key={item.id} className={`relative p-4 bg-slate-950/50 border rounded-lg transition-colors ${trueSurplus > 0 ? 'border-blue-500/30' : 'border-slate-800/50'}`}>
                            
                            {/* Priority Badge & Reorder Controls */}
                            <div className="absolute -left-3 top-4 flex flex-col items-center gap-1">
                                <div className="bg-blue-500 text-slate-900 font-bold w-6 h-6 rounded-full flex items-center justify-center text-xs shadow-lg shadow-blue-500/20 z-10">
                                    {index + 1}
                                </div>
                                <div className="flex flex-col bg-slate-900 border border-slate-700 rounded-full p-0.5 mt-1">
                                    <button 
                                        onClick={() => handleReorder(item.id, 'up')}
                                        disabled={index === 0}
                                        className="p-0.5 text-slate-500 hover:text-blue-400 disabled:opacity-30 disabled:hover:text-slate-500"
                                    >
                                        <ArrowUp className="w-3 h-3" />
                                    </button>
                                    <button 
                                        onClick={() => handleReorder(item.id, 'down')}
                                        disabled={index === data.waterfall.length - 1}
                                        className="p-0.5 text-slate-500 hover:text-blue-400 disabled:opacity-30 disabled:hover:text-slate-500"
                                    >
                                        <ArrowDown className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>

                            <div className="ml-6">
                                <div className="flex justify-between mb-2 items-center">
                                    <input value={item.name} onChange={(e) => actions.updateArrayItem('waterfall', item.id, 'name', e.target.value)} className="bg-transparent text-white font-bold outline-none border-b border-transparent focus:border-blue-500 w-full mr-2" placeholder="Bucket Name" />
                                    <select value={item.linkedAssetId || ""} onChange={(e) => actions.updateArrayItem('waterfall', item.id, 'linkedAssetId', Number(e.target.value) || null)} className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-xs text-emerald-400 focus:border-emerald-500 outline-none max-w-[120px]">
                                        <option value="">Link Asset...</option>
                                        {data.assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </select>
                                </div>
                                <div className="flex gap-2 items-center">
                                    <Layers className="w-4 h-4 text-blue-400" />
                                    <select value={item.targetType} onChange={(e) => actions.updateArrayItem('waterfall', item.id, 'targetType', e.target.value)} className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-xs text-slate-300 focus:border-blue-500 outline-none flex-1">
                                        <option value="total_goal">Fill to Goal Amount</option>
                                        <option value="annual_limit">Fill to Annual Limit (/yr)</option>
                                        <option value="unlimited">Unlimited Overflow</option>
                                    </select>
                                    {item.targetType !== 'unlimited' && (
                                        <input type="number" value={item.targetAmount} onChange={(e) => actions.updateArrayItem('waterfall', item.id, 'targetAmount', parseFloat(e.target.value))} className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-sm w-24 text-white focus:border-blue-500 outline-none font-mono" placeholder="$ Target" />
                                    )}
                                    <button onClick={() => actions.removeArrayItem('waterfall', item.id)} className="text-slate-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                            {index < data.waterfall.length - 1 && <div className="absolute left-0 -bottom-4 w-0.5 h-4 bg-blue-500/30 mx-auto right-0"></div>}
                        </div>
                    ))}
                    <button onClick={() => actions.addArrayItem('waterfall', {id: Date.now(), name: "New Bucket", targetType: "total_goal", targetAmount: 10000, priority: data.waterfall.length + 1})} className="w-full py-3 border-2 border-dashed border-slate-800 rounded-xl text-slate-500 hover:text-blue-400 hover:border-blue-500/30 hover:bg-blue-500/10 transition-all flex items-center justify-center gap-2"><Plus className="w-5 h-5" /> Add Priority Bucket</button>
                    </div>
                </Card>

                {/* RIGHT COLUMN: Visuals + Reservoir */}
                <div className="space-y-6">
                    
                    {/* THE RESERVOIR (SINKING FUNDS) */}
                    <Card element="water">
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center gap-2">
                                <div className="p-2 bg-blue-500/20 rounded-lg"><CalendarDays className="w-5 h-5 text-blue-400"/></div>
                                <div><h3 className="font-bold text-white">The Reservoir</h3><p className="text-xs text-slate-400">Committed savings for future bills.</p></div>
                            </div>
                            <div className="text-right">
                                <span className="block text-[10px] uppercase text-slate-500">Uncommitted Need</span>
                                <span className={`font-mono font-bold ${surplusCalculations.operational < sinkingFundAnalysis.totalMonthlyNeed ? 'text-red-400' : 'text-blue-400'}`}>
                                    {formatCurrency(sinkingFundAnalysis.totalMonthlyNeed)}/mo
                                </span>
                            </div>
                        </div>

                        {surplusCalculations.operational < sinkingFundAnalysis.totalMonthlyNeed && (
                            <div className="mb-4 p-2 bg-red-500/10 border border-red-500/50 rounded flex items-center gap-2 text-red-400 text-xs">
                                <AlertCircle className="w-4 h-4" />
                                <span>Warning: Sinking funds exceed available surplus.</span>
                            </div>
                        )}

                        <div className="space-y-3">
                            {sinkingFundAnalysis.analyzedFunds.map(fund => (
                                <div key={fund.id} className={`p-3 rounded-lg border transition-colors group ${fund.isExpired ? 'bg-red-900/10 border-red-500/50' : 'bg-slate-950 border-slate-800 hover:border-blue-500/30'}`}>
                                    {fund.isExpired && (
                                        <div className="flex items-center justify-between bg-red-500/20 text-red-400 text-[10px] px-2 py-1 rounded mb-2">
                                            <span className="font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3"/> Target Date Reached</span>
                                            <div className="flex gap-2">
                                                <button onClick={() => actions.removeArrayItem('sinkingFunds', fund.id)} className="underline hover:text-white">Remove Fund</button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <div className="flex justify-between items-center mb-2">
                                        <input value={fund.name} onChange={(e) => actions.updateArrayItem('sinkingFunds', fund.id, 'name', e.target.value)} className="bg-transparent font-bold text-sm text-white outline-none w-1/2" />
                                        <div className="flex items-center gap-2">
                                            {fund.isCommitted ? (
                                                <span className="text-[10px] text-emerald-400 flex items-center gap-1 bg-emerald-900/20 px-2 py-0.5 rounded border border-emerald-500/30"><CheckCircle className="w-3 h-3"/> In Flow</span>
                                            ) : (
                                                <button onClick={() => handleCommitToFlow(fund)} className="text-[10px] bg-blue-600 hover:bg-blue-500 text-white px-2 py-1 rounded flex items-center gap-1 transition-colors"><Flame className="w-3 h-3"/> Commit to Flow</button>
                                            )}
                                            <button className="text-slate-500 hover:text-orange-400" title="Withdraw from Fund"><ArrowUpCircle className="w-3 h-3"/></button>
                                            <button onClick={() => actions.removeArrayItem('sinkingFunds', fund.id)} className="opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3 text-slate-600 hover:text-red-400"/></button>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <div>
                                            <label className="text-[10px] text-slate-500 block">Target $</label>
                                            <input type="number" value={fund.amount} onChange={(e) => actions.updateArrayItem('sinkingFunds', fund.id, 'amount', parseFloat(e.target.value))} className="bg-slate-900 w-full text-xs p-1 rounded border border-slate-800 text-white focus:border-blue-500 outline-none" />
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-slate-500 block">Saved $</label>
                                            <input type="number" value={fund.current} onChange={(e) => actions.updateArrayItem('sinkingFunds', fund.id, 'current', parseFloat(e.target.value))} className="bg-slate-900 w-full text-xs p-1 rounded border border-slate-800 text-white focus:border-blue-500 outline-none" />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="text-[10px] text-slate-500 block">Date Needed</label>
                                            <input type="date" value={fund.date ? fund.date.split('T')[0] : ''} onChange={(e) => actions.updateArrayItem('sinkingFunds', fund.id, 'date', e.target.value)} className="bg-slate-900 w-full text-xs p-1 rounded border border-slate-800 text-slate-300 focus:border-blue-500 outline-none" />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-800/50">
                                        <div className="flex-1 h-1.5 bg-slate-900 rounded-full overflow-hidden">
                                            <div className="h-full bg-blue-500" style={{width: `${Math.min(100, fund.progress)}%`}}></div>
                                        </div>
                                        <span className="text-xs font-mono text-blue-300 whitespace-nowrap">{formatCurrency(fund.monthlyContribution)}/mo</span>
                                    </div>
                                </div>
                            ))}
                            <button onClick={() => actions.addArrayItem('sinkingFunds', { id: Date.now(), name: "New Fund", amount: 1000, current: 0, date: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0], isCommitted: false })} className="w-full py-2 border border-dashed border-slate-700 rounded text-xs text-slate-500 hover:text-blue-400 hover:border-blue-500/30 flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> Add Reservoir Fund</button>
                        </div>
                    </Card>

                    {/* FLOW VISUALIZATION */}
                    <Card element="water" className="bg-blue-900/10 border-blue-500/20">
                        <SectionHeader icon={Droplets} title="Current Flow State" subtitle="Monthly Distribution Visualized" element="water" />
                        <div className="mb-6 text-center">
                            <p className="text-sm text-blue-300 uppercase font-bold">Allocatable Surplus</p>
                            <p className={`text-3xl font-bold ${trueSurplus >= 0 ? 'text-white' : 'text-red-400'} animate-pulse`}>{formatCurrency(trueSurplus)}<span className="text-sm text-blue-400">/mo</span></p>
                            <p className="text-[10px] text-slate-400 mt-1">After Reservoir commitments</p>
                        </div>
                        {trueSurplus > 0 ? (
                            <div className="space-y-4 relative">
                                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-transparent opacity-30"></div>
                                {waterfallVisuals.map(bucket => {
                                    const isUnlimited = bucket.targetType === 'unlimited';
                                    const widthPercent = isUnlimited ? '100%' : `${Math.min(100, (bucket.currentVal / bucket.targetAmount)*100)}%`;
                                    const progressLabel = isUnlimited 
                                        ? '∞ Overflow Catch' 
                                        : `${formatCurrency(bucket.currentVal)} / ${formatCurrency(bucket.targetAmount)}`;
                                    const statusLabel = !isUnlimited && bucket.filled > 0 
                                        ? `${bucket.checksNeeded} paychecks` 
                                        : bucket.filled === 0 ? 'Waiting for flow...' : 'Filling indefinitely';

                                    return (
                                    <div key={bucket.id} className="relative pl-8">
                                        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-slate-900 border-2 border-blue-500 rounded-full z-10"></div>
                                        <div className={`p-3 rounded-lg border ${bucket.filled > 0 ? 'bg-blue-500/10 border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.2)]' : 'bg-slate-950/50 border-slate-800/50 opacity-60'}`}>
                                            <div className="flex justify-between text-sm mb-2">
                                                <span className="text-slate-200 font-bold flex items-center gap-2"><Mountain className="w-3 h-3 text-emerald-400"/> {bucket.name}</span>
                                                <span className="text-blue-300 font-mono font-bold">+{formatCurrency(bucket.filled)}/mo</span>
                                            </div>
                                            <div className="w-full bg-slate-900 rounded-full h-3 overflow-hidden mb-1 border border-slate-800 relative">
                                                <div className="h-full bg-gradient-to-r from-blue-600 to-cyan-400 transition-all duration-700 relative overflow-hidden" style={{width: widthPercent}}>
                                                    {bucket.filled > 0 && <div className="absolute inset-0 bg-white/30 animate-shimmer" style={{backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)', backgroundSize: '200% 100%'}}></div>}
                                                </div>
                                            </div>
                                            <div className="flex justify-between items-center text-[10px] text-slate-400">
                                                <span>{progressLabel}</span>
                                                
                                                {/* --- H5 FIX: Updated Visuals to Blue/Water --- */}
                                                {bucket.filled > 0 && (
                                                    <button 
                                                        onClick={() => handleCommitBucket(bucket)} 
                                                        className="text-blue-400 hover:text-white flex items-center gap-1 transition-colors px-2 py-0.5 rounded hover:bg-blue-500/20 border border-blue-500/30"
                                                        title="Lock this in as a fixed expense"
                                                    >
                                                        <CheckCircle className="w-3 h-3" /> Commit
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {bucket.remainingAfter > 0 && <div className="absolute left-4 bottom-[-1rem] text-[10px] text-blue-400 font-mono">↓ {formatCurrency(bucket.remainingAfter)} continues</div>}
                                    </div>
                                    )})}
                            </div>
                        ) : (
                            <div className="text-center p-6 border-2 border-dashed border-slate-800 rounded-xl text-slate-500">
                                <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-xs">No overflow available. Resolve deficit to activate waterfalls.</p>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
};